import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSortUp, faSortDown, faSearch } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import useCommonResponseHandler from '../CommenResponseAndErrorHandlers/CommenResponseHandler';
import { Msg200or201, Msg204, Msg400, Msg401 ,Msg403, Msg500 } from '../Messages/Messages';
import { Loader } from '../Loader/Loader';
import Search from '../Components/Search';
import Pagination, { usePagination } from '../Components/Pagination';
import { decryptData } from '../../utils/storageCrypto';
import { ClipboardDocumentListIcon } from '@heroicons/react/24/outline';


const QuotationsList = () => {
  const [quotations, setQuotations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });
  const {
    showPopup, popupMessage, popupType, retryFunction, setShowPopup, setPopupMessage,
    setPopupType, setRetryFunction, setIsSaving,
  } = useCommonResponseHandler();
  const RETRY_LIMIT = 3;
  const [retryCount, setRetryCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [roleId, setRoleId] = useState(null);
  const [filteredQuotes, setFilteredQuotes] = useState([]);
const searchTypes = ['Quote No', 'Customer Name', 'Item Category', 'Status'];
const [searchType, setSearchType] = useState('');
const [searchChips, setSearchChips] = useState(() => {
  const saved = sessionStorage.getItem('quotationSearchChips');
  return saved ? JSON.parse(saved) : [];
});


 const baseQuotations  = roleId === 6 
? quotations.filter(quotation => (quotation.product_category_id?.product_category_name.toLowerCase()) === 'tiles')
: quotations;
const isSearching = searchChips.length > 0 || searchQuery.trim() !== '' || searchType !== '';

const quotesToDisplay = isSearching ? filteredQuotes : baseQuotations;

const {
  currentItems,
  currentPage,
  totalPages,
  totalItems,
  handlePageChange,
  handlePreviousPage,
  handleNextPage,
  resetToFirstPage
} = usePagination(quotesToDisplay, 10);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Draft':
        return 'text-gray-500';
      case 'Sent':
        return 'text-blue-500';
      case 'Accepted':
        return 'text-green-500';
      default:
        return '';
    }
  };

  const sortArray = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  
    // Function to get nested value using a key
    const getNestedValue = (item, key) => {
      return key.split('.').reduce((value, keyPart) => {
        return value ? value[keyPart] : undefined;
      }, item);
    };
  
    const sortedArray = [...quotations].sort((a, b) => {
      const aValue = getNestedValue(a, key);
      const bValue = getNestedValue(b, key);
  
      // Check if values are numbers
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        if (direction === 'ascending') {
          return aValue - bValue;
        } else {
          return bValue - aValue;
        }
      } else {
        // If values are not numbers, convert to strings and compare
        const aString = aValue.toString().toLowerCase();
        const bString = bValue.toString().toLowerCase();
        if (aString < bString) {
          return direction === 'ascending' ? -1 : 1;
        }
        if (aString > bString) {
          return direction === 'ascending' ? 1 : -1;
        }
        return 0;
      }
    });
  
   setQuotations(sortedArray);
resetToFirstPage();
  };
  

  const getSortIcon = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'ascending' ? faSortUp : faSortDown;
    }
    return null;
  };

 
 useEffect(() => {
 if (quotations.length > 0) {
    if (searchQuery && searchType) {
      handleSearch(searchQuery, searchType);
    } else {
      setFilteredQuotes(baseQuotations);
    }
  }}, [quotations, baseQuotations]); 
  

useEffect(() => {
    const savedSearchChips = JSON.parse(sessionStorage.getItem('quotationSearchChips')) || [];
    setSearchChips(savedSearchChips);
  
    if (savedSearchChips.length > 0) {
      const lastChip = savedSearchChips[0];
      setSearchQuery(lastChip.query);
      setSearchType(lastChip.type);
    }
    fetchRoleId();
    fetchQuotations();
  }, []);

   useEffect(() => {
        if (searchQuery && searchType) {
          const newChip = [{
            query: searchQuery,
            type: searchType
          }];
      
          setSearchChips(newChip);
          sessionStorage.setItem('quotationSearchChips', JSON.stringify(newChip));
        }
      }, [searchQuery, searchType]);

 const handleSearch = (query, type) => {
  if (!query || query.trim() === '') {
    setFilteredQuotes(baseQuotations);
    setSearchChips([]);
    sessionStorage.removeItem('quotationSearchChips');
    resetToFirstPage();
    return;
  }

  const lowercaseQuery = (query || '').toLowerCase();
  const filtered = baseQuotations.filter((quotation) => {
    switch (type) {
      case 'Quote No':
        return quotation?.quotation_number?.toLowerCase().includes(lowercaseQuery);
      case 'Customer Name':
        return quotation?.customer_id?.customer_name?.toLowerCase().includes(lowercaseQuery);
      case 'Item Category':
        return quotation?.product_category_id?.product_category_name?.toLowerCase().includes(lowercaseQuery);
      case 'Status':
        return quotation?.status?.toLowerCase().includes(lowercaseQuery);
      default:
        return false;
    }
  });

  setFilteredQuotes(filtered);
  resetToFirstPage();
};


 const handleRemoveSearchChip = () => {
  setSearchChips([]);
  sessionStorage.removeItem('quotationSearchChips');

  setSearchQuery('');
  setSearchType('');
  setFilteredQuotes(baseQuotations); 

};

 
 const handleAddSearchChip = (query, type) => {
  if (!type) return;

  const actualQuery = query.trim();

  const newChip = [{ query: actualQuery, type }]; // only one chip

  setSearchChips(newChip);
  sessionStorage.setItem('quotationSearchChips', JSON.stringify(newChip));

  setSearchQuery(actualQuery);
  setSearchType(type);

  handleSearch(actualQuery, type);
};

const handleChipClick = (chip) => {
  setSearchType(chip.type);
  setSearchQuery(chip.query);
  handleSearch(chip.query, chip.type);
};

  const fetchRoleId = () => {
    const enroleId = sessionStorage.getItem('roleId');
    const sroleId = decryptData(enroleId)
    setRoleId(sroleId);
  };

  const fetchQuotations = async (attempt = 0) => {
    try {
      const encryptedToken = sessionStorage.getItem('token');
                const token = decryptData(encryptedToken);
                const encryptedOrganizationId = sessionStorage.getItem('organizationId');
                const organizationId = decryptData(encryptedOrganizationId);
      const response = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}customer/quotations/get-all-quotations?organizationId=${organizationId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-token': token,
        },
      });

      const data = await response.json();
if (response.ok) {
  setQuotations(data.data || []);
  console.log(data.data);
} else {
  console.error(data.message);
}


      if (response.status === 500) {
        setRetryFunction(() => () => fetchQuotations(attempt));
      }
    } catch (error) {
      console.error('Failed to fetch quotations:', error);

      if (attempt < RETRY_LIMIT) {
        setRetryCount(attempt + 1);
        setTimeout(() => fetchQuotations(attempt + 1), 1000);
      } else {
        setIsLoading(false);
        setPopupMessage('Internal Server Error. Kindly Retry.');
        setPopupType('500');
        setShowPopup(true);
        setRetryFunction(() => () => fetchQuotations(attempt));
      }
    } finally {
      setIsLoading(false);
    }
  };

  console.log('Quotations:', quotations);


  const handleCreate = () => {
    navigate("/bb/app/sales/newquote");
  };

  const handleNavigateToQuoteDetails = (quotation) => {
    navigate("/bb/app/sales/quotedetails", {state:{QuoteData:quotation}});
    console.log(quotation);
  }

  const formatNumber = (value) => {
    // Parse the input as a number
    const parsedValue = parseFloat(value);
  
    // Check if the parsed value is a finite number
    if (Number.isFinite(parsedValue)) {
      // Round to two decimal places
      const roundedValue = (Math.round(parsedValue * 100) / 100);
    // Format the number with commas and two decimal places
    return roundedValue.toLocaleString('hi-IN', { minimumFractionDigits: 2 }); 
    }
  
    // Return '0.00' if the value is not a valid number
    return '0.00';
  };

  if (isLoading) {
    return <Loader />;
  }

  
  const closePopup = () => {
    console.log("closePopup called");
    setShowPopup(false);
  };
  
  const Retry = async() => {
    console.log("Retry called");
    setTimeout(async ()=>{
      if(retryFunction){
  
        await retryFunction ();
      }
  
      setIsLoading(false)
    },1000)
    
    setShowPopup(false);
  };
  
  const BackToLoginPage = () => {
    console.log("Back To Login Page");
    setShowPopup(false);
    localStorage.removeItem('LoginData');
    sessionStorage.removeItem('userData');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('userId');
    sessionStorage.removeItem('appId');
  navigate('/bb');
  };

 
  return (
    <div className="bg-white flex flex-row w-full justify-left h-full overflow-y-hidden">
      <div className="flex flex-row w-full">
        <div className="w-full flex-1">
          <div className="flex flex-col w-full h-full justify-center items-center">
            <div className="w-full h-full flex flex-col justify-between">
              <div className="w-full h-16 border-b flex items-center">
                <div className="flex w-full justify-between px-5 items-center">
                <div className='space-x-10 flex flex-row mt-4 mb-4'>
                  <h1 className="text-lg font-semibold whitespace-nowrap">Quote Lists</h1>
                  {quotations.length > 0 && (
                    <>
                  <Search
  searchQuery={searchQuery}
  setSearchQuery={setSearchQuery}
  searchType={searchType}
  setSearchType={setSearchType}
  searchTypes={searchTypes}
  onSearch={handleSearch}
  onSearchSubmit={handleAddSearchChip}
  placeholder="Search..."
/>
                 

{searchChips.length > 0 && (
  <div className="w-full flex flex-cols gap-3 items-center mt-1 px-4">
    {searchChips.map((chip, index) => {
      const isActive = chip.type === searchType;

      const chipClasses = `flex items-center max-w-xs text-sm px-3 py-1 rounded-full shadow-sm transition-all duration-150 ${
        isActive
          ? 'bg-red-500 text-white'
          : 'bg-gray-100 text-gray-800'
      }`;

      return (
        <div
           key={`${chip.type}-${chip.query}-${index}`} // Better unique key
      className={chipClasses}
      style={{ whiteSpace: 'nowrap' }}
      onClick={() => handleChipClick(chip)}
    >
      <span className="whitespace-nowrap ">
        {chip.type}: {chip.query}
      </span>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleRemoveSearchChip(chip);
            }}
            className={`ml-2 text-base font-bold ${
              isActive ? 'text-white hover:text-gray-200' : 'text-gray-500 hover:text-red-500'
            }`}
          >
            ×
          </button>
        </div>
      );
    })}
  </div>
)}

</>
 )}


                        </div>
                  <button onClick={handleCreate} className="bg-[#F7525A] text-white z-20 px-2 py-1 rounded"
                    title="" data-tooltip="Create Quote"  style={{ '--tooltip-bg': 'black' }}>+ New</button>
                </div>
              </div>

            {/* Pagination Info Below Header */}
<Pagination
  currentPage={currentPage}
  totalPages={totalPages}
  totalItems={totalItems}
  itemsPerPage={10}
  showInfo={true}
  hideControls={true} 
  itemName="quotations"
/>

            <div className="w-full h-full bg-white rounded-xs overflow-hidden">
  <div className="overflow-y-auto h-[100vh]">
    {baseQuotations && Array.isArray(baseQuotations) && baseQuotations.length > 0 ? (
      <table className="w-full mb-5">
        <thead className="sticky top-0 z-10 bg-[#fafafc] text-[#6c7184] border-b">
          <tr className='border-b'>
            <th className="text-xs py-3 text-center">
              <input type="checkbox" className="mr-2" />
            </th>
            <th className="text-xs py-3 text-center cursor-pointer" onClick={() => sortArray('quotation_date')}>
              DATE {getSortIcon('quotation_date') && <FontAwesomeIcon icon={getSortIcon('quotation_date')} />}
            </th>
            <th className="text-xs py-3 text-center cursor-pointer" onClick={() => sortArray('quotation_number')}>
              QUOTE No {getSortIcon('quotation_number') && <FontAwesomeIcon icon={getSortIcon('quotation_number')} />}
            </th>
            <th className="text-xs py-3 text-center cursor-pointer" onClick={() => sortArray('customer_id.customer_name')}>
              CUSTOMER NAME {getSortIcon('customer_id.customer_name') && <FontAwesomeIcon icon={getSortIcon('customer_id.customer_name')} />}
            </th>
            <th className="text-xs py-3 text-center cursor-pointer" onClick={() => sortArray('product_category_id.product_category_name')}>
              ITEM CATEGORY {getSortIcon('product_category_id.product_category_name') && <FontAwesomeIcon icon={getSortIcon('product_category_id.product_category_name')} />}
            </th>
            <th className="text-xs py-3 text-center cursor-pointer" onClick={() => sortArray('status')}>
              STATUS {getSortIcon('status') && <FontAwesomeIcon icon={getSortIcon('status')} />}
            </th>
            <th className="text-xs py-3 text-center">
              AMOUNT
            </th>
          </tr>
        </thead>
        <tbody className='border-b border-t'>
          {currentItems.length > 0 ? (
            currentItems.map((quotation) => (
              <tr key={quotation?._id} className="text-sm border-b">
                <td className="py-2 text-center">
                  <input type="checkbox" className="mr-2" />
                </td>
                <td className="py-2 text-center ">
                  {quotation?.quotation_date ? new Date(quotation.quotation_date).toLocaleDateString() : 'N/A'}
                </td>
                <td className="py-2 text-center text-[#408dfb] font-500 cursor-pointer" onClick={() => handleNavigateToQuoteDetails(quotation)}>
                  {quotation?.quotation_number || 'N/A'}
                </td>
                <td className="py-2 text-center ">
                  {quotation?.customer_id?.customer_name || 'N/A'}
                </td>
                <td className="py-2 text-center">
                  {quotation?.product_category_id?.product_category_name || 'N/A'}
                </td>
                <td className={`py-2 text-center ${getStatusColor(quotation?.status)}`}>
                  {quotation?.status || 'N/A'}
                </td>
                <td className="py-2 text-center">
                  {quotation?.total ? formatNumber(quotation.total) : 'N/A'}
                </td>
              </tr>
            ))
          ) : isSearching ? (
            <tr>
              <td colSpan="7" className="text-center py-8">
                <div className="flex flex-col items-center">
                  <FontAwesomeIcon icon={faSearch} className="text-4xl text-gray-300 mb-4" />
                  <p className="text-lg font-medium mb-2 text-gray-700">No result found.</p>
                  <p className="text-sm text-gray-500">Try adjusting your search criteria.</p>
                </div>
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    ) : (
      <>
        {/* <div className="m-4 p-4 text-center border border-gray-300 text-gray-500">
          No Quotations Found.
        </div> */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-8 mx-4 mt-4">
                            <div className="text-center text-gray-500">
                              <div className="mb-4">
                                <ClipboardDocumentListIcon className="mx-auto h-12 w-12 text-gray-300" />
                              </div>
                              <h3 className="text-lg font-medium mb-2">No Quotations Found</h3>
                              <p className="text-sm">Quotations will appear once they are created.</p>
                            </div>
                             <div className="flex justify-center items-center mt-4">
                                  <button className="bg-[#F7525A] text-white px-2 py-1 rounded-md" onClick={handleCreate}>Create</button>
                                </div>
                          </div>
      </>
    )}
  </div>
</div>


              
             <div className="sticky bottom-0 bg-white shadow-md z-10 px-4 py-2">
<Pagination
  currentPage={currentPage}
  totalPages={totalPages}
  totalItems={totalItems}
  itemsPerPage={10}
  onPageChange={handlePageChange}
  onPreviousPage={handlePreviousPage}
  onNextPage={handleNextPage}
  showInfo={false}
  itemName="quotations"
/>
</div>
              
            </div>
          </div>
        </div>
      </div>
      {showPopup && (
  <div>
    {popupType === '200or201' && <Msg200or201 message={popupMessage} timeout={3000} />}
    {popupType === '204' && <Msg204 message={popupMessage} timeout={3000} />}
    {popupType === '400' && <Msg400 message={popupMessage} closePopup={closePopup} />}
          {popupType === '401' && <Msg401 message={popupMessage} closePopup={closePopup} />}
          {popupType === '403' && <Msg403 message={popupMessage} closePopup={BackToLoginPage} />}
          {popupType === '500' && <Msg500 message={popupMessage} closePopup={Retry} cancelPopup={closePopup}/>}
  </div>
)}
    </div>
  );
};

export default QuotationsList;
